import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import PropertiesTable from "@/components/PropertiesTable/PropertiesTable";
import ApiError from "@/model/ApiError";
import PropertyResponse from "@/model/PropertyResponse";
import "@/components/CompanyPropertiesPanel/CompanyPropertiesPanel.scss";

const PAGE_SIZE = 20;

interface CompanyPropertiesPanelProps {
  companyId: string;
  addLinkTo: string;
}

function CompanyPropertiesPanel({ companyId, addLinkTo }: CompanyPropertiesPanelProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [properties, setProperties] = useState<PropertyResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    propertiesApi
      .listPropertiesForCompany(session.accessToken, companyId, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setProperties(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, companyId, page]);

  if (!session) {
    return null;
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("properties.panel.deleteConfirm"))) {
      return;
    }

    setDeletingId(id);
    setDeleteError(null);

    try {
      await propertiesApi.deleteProperty(session.accessToken, id);
      setProperties((current) => current.filter((property) => property.id !== id));
    } catch (err) {
      setDeleteError(
        err instanceof ApiError && err.status === 409
          ? t("properties.panel.deleteHasBookingsError")
          : t("properties.panel.deleteError")
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="company-properties-panel">
      <div className="company-properties-panel__header">
        <h2>{t("properties.panel.title")}</h2>
        <Link to={addLinkTo}>{t("properties.panel.addLink")}</Link>
      </div>

      {deleteError && (
        <p className="company-properties-panel__error">{deleteError}</p>
      )}

      {isLoading && <p>{t("properties.panel.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("properties.panel.error")}</p>}
      {!isLoading && !error && properties.length === 0 && (
        <p>{t("properties.panel.empty")}</p>
      )}

      {!isLoading && !error && properties.length > 0 && (
        <>
          <PropertiesTable
            properties={properties}
            renderActions={(property) =>
              property.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(property.id as string)}
                  disabled={deletingId === property.id}
                >
                  {deletingId === property.id
                    ? t("properties.panel.deleting")
                    : t("properties.panel.deleteButton")}
                </button>
              )
            }
          />

          <div className="company-properties-panel__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", { page: page + 1, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default CompanyPropertiesPanel;
export type { CompanyPropertiesPanelProps };
